import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestaurantIcon from "@mui/icons-material/Restaurant";

import PageLayout from "examples/LayoutContainers/PageLayout";
import Ingredients from "./components/Ingredients";
import Recipe1Page from "./components/RecipeDetails";
import ScheduleEditControls from "./components/ScheduleEditControls";
import SlotSwapPanel from "components/SlotSwapPanel";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";

import WeekCompleteMoment from "./components/WeekCompleteMoment";
import { RED, RED_LIGHT, GREEN } from "assets/theme/brandColors";
import { MEAL_TYPE_BADGE as MEAL_TYPE_TAB_STYLE } from "utils/mealTypes";

const TOPBAR_H = 57;
const TABNAV_H = 52;

function TabBtn({ active, onClick, icon, label, mealType }) {
  const tag = MEAL_TYPE_TAB_STYLE[mealType];
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2.5,
        py: 1.75,
        fontSize: 14,
        fontFamily: "Roboto, sans-serif",
        fontWeight: active ? 700 : 600,
        color: active ? RED : "#666",
        borderBottom: "3px solid",
        borderColor: active ? RED : "transparent",
        cursor: "pointer",
        whiteSpace: "nowrap",
        userSelect: "none",
        transition: "color 0.15s, border-color 0.15s",
        "&:hover": { color: active ? RED : "#333" },
      }}
    >
      {icon}
      {label}
      {tag && (
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            px: "5px",
            py: "1px",
            borderRadius: "4px",
            bgcolor: tag.bg,
            color: tag.color,
            ml: 0.5,
            lineHeight: "16px",
          }}
        >
          {tag.label}
        </Box>
      )}
    </Box>
  );
}

TabBtn.propTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  mealType: PropTypes.string,
};

function ModulePage() {
  const { state } = useLocation();
  const { programId } = useParams();

  // Which recipe tab is open survives a reload — on mobile a refresh used to
  // drop the user back on the Grocery List, losing the recipe they were
  // cooking from. Explicit navigation state still wins over the stored tab.
  const tabStorageKey = programId ? `estia_active_tab_${programId}` : null;
  const [activeTab, setActiveTab] = useState(() => {
    if (state?.initialRecipe) return state.initialRecipe;
    try {
      return (tabStorageKey && sessionStorage.getItem(tabStorageKey)) || "grocery";
    } catch {
      return "grocery";
    }
  });

  const openTab = (tab) => {
    setActiveTab(tab);
    try {
      if (tabStorageKey) sessionStorage.setItem(tabStorageKey, tab);
    } catch {
      /* storage unavailable — tab just won't survive a reload */
    }
    window.scrollTo({ top: 0 });
  };

  const [recipes, setRecipes] = useState([]);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [programSaved, setProgramSaved] = useState(false);
  const [savingProgram, setSavingProgram] = useState(false);
  const [programTitle, setProgramTitle] = useState("");
  const [snackMsg, setSnackMsg] = useState("");
  // Repair state for an uncooked week: which slot has the swap panel open, and
  // which recipe is mid-removal.
  const [swapSlot, setSwapSlot] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  // The week celebration, and the summary waiting for the recipe's own reward
  // moment to be dismissed first.
  const [pendingWeek, setPendingWeek] = useState(null);
  const [weekSummary, setWeekSummary] = useState(null);
  // Bumped after a replace or a remove so the grocery list re-fetches and
  // re-aggregates against the new set of recipes — the same aggregation path
  // the servings steppers already use, just with a different recipe list.
  const [groceryVersion, setGroceryVersion] = useState(0);
  const navigate = useNavigate();

  // servingsMap: { [recipeId]: number } — persisted in localStorage per program
  const storageKey = programId ? `estia_servings_${programId}` : null;
  const [servingsMap, setServingsMap] = useState(() => {
    if (!storageKey) return {};
    try {
      return JSON.parse(localStorage.getItem(storageKey)) || {};
    } catch {
      return {};
    }
  });

  const updateServings = (recipeId, value) => {
    setServingsMap((prev) => {
      const next = { ...prev, [String(recipeId)]: value };
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  // How many people this week was planned for — a property of the schedule,
  // which every recipe in it inherits until the user overrides that one
  // recipe. The server records it (`program_servings`); the localStorage entry
  // is the fallback for weeks built before it did.
  const [scheduleServings, setScheduleServings] = useState(() => {
    if (!programId) return null;
    const stored = localStorage.getItem(`estia_servings_default_${programId}`);
    const n = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  useEffect(() => {
    if (!getAuthToken()) navigate("/authentication/sign-in", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!programId) return;
    (async () => {
      try {
        const [recipesRes, completedRes, savedRes] = await Promise.all([
          apiClient.get(ENDPOINTS.schedule.programTitles(programId)),
          apiClient.get(ENDPOINTS.schedule.programCompletedRecipes(programId)),
          apiClient.get(ENDPOINTS.collections.programSave(programId)),
        ]);
        if (Array.isArray(recipesRes.data)) {
          setRecipes(recipesRes.data);
          if (recipesRes.data.length > 0) {
            setProgramTitle(recipesRes.data[0].program_title || "");
            const fromServer = recipesRes.data[0].program_servings;
            if (Number.isFinite(fromServer) && fromServer > 0) setScheduleServings(fromServer);
          }
        }
        if (Array.isArray(completedRes.data))
          setCompletedIds(new Set(completedRes.data.map(Number)));
        setProgramSaved(savedRes.data.saved);
      } catch (err) {
        console.error("Failed to fetch module data:", err.message);
      }
    })();
  }, [programId]);

  // Every recipe in the week inherits the week's servings unless the user has
  // set that one recipe itself. Keyed on `recipes`, not on the initial load,
  // so a recipe swapped in after the build inherits it too — seeding once at
  // mount is what let a replacement arrive at its own 6 servings inside a
  // 2-serving schedule and slide the grocery header from 2 to 3.
  useEffect(() => {
    if (!scheduleServings || recipes.length === 0) return;
    setServingsMap((prev) => {
      const seeded = { ...prev };
      let changed = false;
      recipes.forEach((r) => {
        const key = String(r.id);
        if (!(key in seeded)) {
          seeded[key] = scheduleServings;
          changed = true;
        }
      });
      if (changed && storageKey) localStorage.setItem(storageKey, JSON.stringify(seeded));
      return changed ? seeded : prev;
    });
  }, [recipes, scheduleServings]);

  // A restored tab can point at a recipe that is no longer in the program
  // (replaced in the builder, schedule edited elsewhere).
  useEffect(() => {
    if (activeTab === "grocery" || recipes.length === 0) return;
    if (!recipes.some((r) => String(r.id) === activeTab)) openTab("grocery");
  }, [recipes]);

  const handleSaveProgram = async () => {
    if (savingProgram) return;
    if (doneCount === 0 && !programSaved) {
      setSnackMsg("Cook at least one recipe from this schedule before saving it.");
      return;
    }
    setSavingProgram(true);
    try {
      // Idempotent both ways — each call sets the saved flag to one fixed
      // value — so apiClient may retry them after a dropped connection.
      if (programSaved) {
        await apiClient.delete(ENDPOINTS.collections.programSave(programId), { idempotent: true });
        setProgramSaved(false);
        setSnackMsg("Removed this schedule from your Cookbook.");
      } else {
        await apiClient.post(ENDPOINTS.collections.programSave(programId), null, {
          idempotent: true,
        });
        setProgramSaved(true);
        setSnackMsg("Saved this schedule to your Cookbook.");
      }
    } catch (err) {
      console.error("Save program failed:", err.message);
      setSnackMsg(
        err.response?.data?.detail ||
          "Couldn't reach your Cookbook — your schedule wasn't saved. Try again."
      );
    } finally {
      setSavingProgram(false);
    }
  };

  // Save-only path for the week celebration: that button is the user saying
  // they liked the week, never a toggle that could quietly unsave it.
  const saveProgramToCookbook = async () => {
    try {
      await apiClient.post(ENDPOINTS.collections.programSave(programId), null, {
        idempotent: true,
      });
      setProgramSaved(true);
      return true;
    } catch (err) {
      console.error("Save program failed:", err.message);
      setSnackMsg(
        err.response?.data?.detail ||
          "Couldn't reach your Cookbook — your schedule wasn't saved. Try again."
      );
      return false;
    }
  };

  const handleComplete = (recipeId, result) => {
    setCompletedIds((prev) => new Set([...prev, Number(recipeId)]));
    // Held, not shown: the recipe's own reward moment goes first, and hands
    // over when it is dismissed. Two celebrations stacked is one celebration
    // covering another.
    if (result?.schedule_completed && result?.schedule_summary) {
      setPendingWeek(result.schedule_summary);
    }
  };

  const showWeekMoment = () => {
    if (!pendingWeek) return;
    setWeekSummary(pendingWeek);
    setPendingWeek(null);
  };

  // A swap keeps the slot: the new recipe takes the old one's place in the tab
  // strip, and the view follows it so the user lands on what they just chose.
  const handleReplaced = (newRecipe, slot) => {
    setRecipes((prev) =>
      prev.map((r) =>
        String(r.id) === String(slot.id)
          ? {
              ...r,
              id: newRecipe.id,
              recipe_title: newRecipe.recipe_title,
              meal_type: newRecipe.meal_type,
              is_completed: false,
            }
          : r
      )
    );
    setGroceryVersion((v) => v + 1);
    openTab(String(newRecipe.id));
    setSnackMsg(`Swapped in “${newRecipe.recipe_title}”.`);
  };

  // Remove shrinks the week — no backfill. Completions, XP, technique reps and
  // goal progress live on the user, not on the schedule row, so nothing
  // already earned is at stake here.
  const handleRemove = async (recipe) => {
    if (removingId) return;
    setRemovingId(recipe.id);
    try {
      const res = await apiClient.delete(ENDPOINTS.schedule.removeRecipe(programId, recipe.id));
      setRecipes((prev) => prev.filter((r) => String(r.id) !== String(recipe.id)));
      setGroceryVersion((v) => v + 1);
      openTab("grocery");
      const left = res.data?.remaining_count;
      setSnackMsg(
        left != null
          ? `Removed. Your week is now ${left} recipe${left === 1 ? "" : "s"}.`
          : "Removed from your week."
      );
    } catch (err) {
      setSnackMsg(err.response?.data?.detail || "Couldn't remove that recipe.");
    } finally {
      setRemovingId(null);
    }
  };

  const doneCount = completedIds.size;
  const total = recipes.length;
  const pct = total > 0 ? (doneCount / total) * 100 : 0;
  const allCompleted = total > 0 && doneCount === total;

  // The week is editable until its first completion. One rule covers both
  // halves of it: a completed or part-cooked schedule has completions in it, so
  // it is locked by the same condition — there is no separate "past schedule"
  // state to keep in sync. The backend enforces this independently.
  const weekUnlocked = total > 0 && doneCount === 0;

  if (!programId) return null;

  return (
    <PageLayout background="#eaecf0">
      {/* ── Fixed header (topbar + tab nav in one container) ── */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          bgcolor: RED,
          boxShadow: "0 2px 6px rgba(160,28,39,0.3)",
        }}
      >
        {/* Topbar row */}
        <Box
          sx={{
            height: TOPBAR_H,
            px: { xs: 2, md: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Back */}
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "rgba(255,255,255,0.9)",
              cursor: "pointer",
              fontSize: 14,
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              "&:hover": { color: "#fff" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 17, color: "rgba(255,255,255,0.9)" }} />
            {state?.from ?? "Back"}
          </Box>

          {/* Center */}
          <Box sx={{ textAlign: "center", flex: 1, px: 2, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#fff",
                fontFamily: "Roboto, sans-serif",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {programTitle || "My Schedule"}
            </Box>
            {total > 0 && (
              <Box
                sx={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.7)",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                {doneCount}/{total} done
              </Box>
            )}
          </Box>

          {/* Progress pill — desktop only */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
              px: 1,
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 5,
                bgcolor: "rgba(255,255,255,0.4)",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${pct}%`,
                  height: "100%",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  transition: "width 0.4s",
                }}
              />
            </Box>
            <Box
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.85)",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              {doneCount} / {total}
            </Box>
          </Box>

          {/* Saves the whole schedule. It used to read "Save", next to a
              bookmark that saved a recipe and a modal that saved a different
              one — three controls, none of which said what they kept. */}
          <Tooltip
            describeChild
            title={
              programSaved
                ? "This schedule is in your Cookbook — click to remove it"
                : "Keep this schedule in your Cookbook"
            }
          >
            <Box
              component="button"
              onClick={handleSaveProgram}
              disabled={savingProgram}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
                cursor: "pointer",
                border: "1.5px solid",
                transition: "all 0.2s",
                ...(programSaved
                  ? {
                      bgcolor: "rgba(0,0,0,0.25)",
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.4)",
                    }
                  : {
                      bgcolor: "#fff",
                      color: RED,
                      borderColor: "#fff",
                      "&:hover": { bgcolor: RED_LIGHT },
                    }),
              }}
            >
              {programSaved ? "★ Schedule saved" : "☆ Save schedule"}
            </Box>
          </Tooltip>
        </Box>

        {/* Tab nav row */}
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              height: TABNAV_H,
              borderBottom: "1px solid #e0e0e0",
              bgcolor: "#fff",
              display: "flex",
              overflowX: "auto",
              px: 0.5,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            <TabBtn
              active={activeTab === "grocery"}
              onClick={() => openTab("grocery")}
              icon={<ShoppingCartIcon sx={{ fontSize: 15, color: "inherit" }} />}
              label="Grocery List"
            />
            {recipes.map((r) => (
              <TabBtn
                key={r.id}
                active={activeTab === String(r.id)}
                onClick={() => openTab(String(r.id))}
                icon={
                  completedIds.has(Number(r.id)) ? (
                    <Box component="span" sx={{ fontSize: 13, color: GREEN, fontWeight: 700 }}>
                      ✓
                    </Box>
                  ) : (
                    <RestaurantIcon sx={{ fontSize: 15, color: "inherit" }} />
                  )
                }
                label={r.recipe_title}
                mealType={r.meal_type}
              />
            ))}
          </Box>
          {/* Right-fade scroll affordance */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 48,
              height: TABNAV_H,
              background: "linear-gradient(to right, transparent, #fff)",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>

      {/* ── Scrollable content ── */}
      <Box
        sx={{
          pt: `${TOPBAR_H + TABNAV_H + 24}px`,
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, md: 3 },
          pb: 4,
        }}
      >
        {activeTab === "grocery" && (
          <Ingredients
            programId={programId}
            programTitle={programTitle}
            servingsMap={servingsMap}
            onServingsChange={updateServings}
            onSelectRecipe={(recipeId) => openTab(String(recipeId))}
            refreshKey={groceryVersion}
          />
        )}

        {recipes.map((r) =>
          activeTab === String(r.id) ? (
            <Box key={r.id}>
              <Recipe1Page
                programId={programId}
                // Repair controls, rendered inside the recipe header under the
                // title. Gone the moment the first recipe is completed — the
                // copy line travels with them and says so before it happens.
                headerSlot={
                  weekUnlocked ? (
                    <ScheduleEditControls
                      recipe={r}
                      canRemove={total > 1}
                      busy={removingId === r.id}
                      onReplace={() => setSwapSlot(r)}
                      onRemove={() => handleRemove(r)}
                    />
                  ) : null
                }
                recipeId={r.id}
                isCompleted={completedIds.has(Number(r.id))}
                onMarkCompleted={(result) => handleComplete(r.id, result)}
                onRewardDismissed={showWeekMoment}
                servings={servingsMap[String(r.id)] ?? null}
                onServingsChange={(v) => updateServings(r.id, v)}
              />
            </Box>
          ) : null
        )}
      </Box>

      {/* The schedule builder's swap panel, unchanged: same alternatives, same
          reasoning annotations, same endpoints. */}
      {swapSlot && (
        <SlotSwapPanel
          programId={programId}
          slot={swapSlot}
          onReplaced={handleReplaced}
          onError={(msg) => setSnackMsg(msg)}
          onClose={() => setSwapSlot(null)}
        />
      )}
      {/* The week is the climax of a weekly product, so it gets the bigger
          moment — and the "build next week" call to action lives in it rather
          than only on a dashboard card. */}
      <WeekCompleteMoment
        summary={weekSummary}
        saved={programSaved}
        onSave={saveProgramToCookbook}
        onClose={() => setWeekSummary(null)}
      />

      <Snackbar
        open={!!snackMsg}
        autoHideDuration={3000}
        onClose={() => setSnackMsg("")}
        message={snackMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </PageLayout>
  );
}

export default ModulePage;
